import { Button, InputNumber, Switch, Table, message } from "antd";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import {
  useFetchOperatingStatesQuery,
  useBulkUpdateOperatingStatesMutation,
} from "features/operatingStates/agencyPortal/operatingStatesAPI";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { getColumnSearchProps } from "common/antd-utils";

const StyledContainer = styled.div`
  width: 100%;
  height: 89vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const StyledTable = styled(Table)`
  .ant-table-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .ant-table {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .ant-table-container {
    flex: 1;
    overflow: hidden;
    min-height: 89vh;
  }

  .ant-table-body {
    overflow-y: auto !important;
  }

  .ant-table-tbody > tr > td {
    padding: 8px 16px;
  }
`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  min-height: 50px;
  padding: 10px 0 0;
`;

const StyledSaveButton = styled(Button)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

export default function OperatingStatesTable() {
  const [operatingStatesData, setOperatingStatesData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);

  const { data: operatingStates, isLoading: isOperatingStatesLoading } =
    useFetchOperatingStatesQuery();
  const { data: constants, isLoading: isConstantsLoading } = useFetchBackendConstantsQuery();
  const [bulkUpdateOperatingStates, { isLoading: isUpdating }] =
    useBulkUpdateOperatingStatesMutation();

  // Create a map of existing operating states
  const operatingStatesMap = useMemo(() => {
    if (!operatingStates) return {};
    // operatingStates is the array directly after transformResponse
    return operatingStates.reduce((acc, state) => {
      acc[state.state] = state;
      return acc;
    }, {});
  }, [operatingStates]);

  // Initialize data when constants and operating states are loaded
  useEffect(() => {
    if (constants?.states) {
      const allStatesData = constants.states.map(({ value, display }) => {
        const existingOperatingState = operatingStatesMap[value];
        return {
          key: value,
          state: value,
          stateName: display,
          active: existingOperatingState?.active || false,
          statuteOfLimitationYears: existingOperatingState?.statuteOfLimitationYears || null,
          id: existingOperatingState?.id || null,
        };
      });
      setOperatingStatesData(allStatesData);
    }
  }, [constants?.states, operatingStates, operatingStatesMap]);

  // Handle search
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const handleActiveChange = useCallback((state, checked) => {
    setOperatingStatesData((prev) =>
      prev.map((item) => (item.state === state ? { ...item, active: checked } : item)),
    );
    setHasChanges(true);
  }, []);

  const handleStatuteChange = useCallback((state, value) => {
    setOperatingStatesData((prev) =>
      prev.map((item) =>
        item.state === state ? { ...item, statuteOfLimitationYears: value } : item,
      ),
    );
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    const operatingStatesToUpdate = operatingStatesData
      .filter((item) => item.id) // Only send items that have IDs (existing states)
      .map((item) => ({
        id: item.id,
        active: item.active,
        statuteOfLimitationYears: item.statuteOfLimitationYears,
      }));

    const result = await bulkUpdateOperatingStates({
      operating_states: operatingStatesToUpdate,
    });

    if ("data" in result) {
      message.success("Operating states updated successfully");
      setHasChanges(false);
    }
  }, [operatingStatesData, bulkUpdateOperatingStates]);

  const columns = [
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      width: 80,
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={(checked) => handleActiveChange(record.state, checked)}
        />
      ),
      sorter: (a, b) => b.active - a.active,
    },
    {
      title: "State",
      dataIndex: "stateName",
      key: "stateName",
      sorter: (a, b) => a.stateName.localeCompare(b.stateName),
      render: (stateName, record) => `${stateName} (${record.state})`,
      sortDirections: ["ascend", "descend"],
      ...getColumnSearchProps("stateName", searchInput, handleSearch, handleReset),
      filteredValue: filteredInfo?.stateName ?? null,
      onFilter: (value, record) => {
        const searchLower = value.toLowerCase();
        return (
          record.stateName.toLowerCase().includes(searchLower) ||
          record.state.toLowerCase().includes(searchLower)
        );
      },
    },
    {
      title: "Statute of Limitations",
      dataIndex: "statuteOfLimitationYears",
      key: "statuteOfLimitationYears",
      width: 350,
      render: (value, record) => (
        <InputNumber
          placeholder="Years"
          min={0}
          max={100}
          value={value}
          onChange={(newValue) => handleStatuteChange(record.state, newValue)}
          style={{ width: "100%" }}
        />
      ),
    },
  ];

  const isLoading = isOperatingStatesLoading || isConstantsLoading;

  return (
    <StyledContainer>
      <TableWrapper>
        <StyledTable
          loading={isLoading || isUpdating}
          bordered
          scroll={{ y: "calc(89vh - 120px)" }}
          pagination={false}
          // @ts-ignore
          columns={columns}
          dataSource={operatingStatesData}
          onChange={handleChange}
        />
      </TableWrapper>

      <SaveButtonContainer>
        <StyledSaveButton
          type="primary"
          size="large"
          onClick={handleSave}
          loading={isUpdating}
          disabled={!hasChanges}
        >
          Save
        </StyledSaveButton>
      </SaveButtonContainer>
    </StyledContainer>
  );
}
